import type { AxiosRequestConfig } from "axios"

import axios from "axios"
import dayjs from "dayjs"
import { toast } from "sonner"
import { paths } from "@/src/routes/paths"
import { showBadInternetToast, dismissBadInternetToast } from "@/src/components/badInternetToast/badInternetToast"

import { CONFIG } from "src/config-global"

// ----------------------------------------------------------------------
const REQUEST_RETRY_HEADER = "Pph-Token-Retry"
const REQUEST_API_TIMEOUT_RETRY_HEADER = "Pph-Token-Api-Timeout-Retry"

const axiosInstance = axios.create({ baseURL: CONFIG.site.serverUrl })

// Convert all top-level dates to UTC
axiosInstance.interceptors.request.use((req) => {
    if (req.method === "post" || req.method === "put") {
        try {
            const dataJson = req.data
            Object.entries(dataJson).forEach(([key, value]) => {
                if (key.toLowerCase().includes("date")) {
                    if (typeof value === "string" && dayjs(value).isValid()) {
                        dataJson[key] = dayjs(value).toISOString()
                    }
                }
            })
            req.data = dataJson
        } catch (e) {
            return req
        }
    }
    return req
})

axiosInstance.interceptors.response.use(
    (response) => {
        dismissBadInternetToast()
        return response
    },
    (error) => {
        const statusCode = error.response?.status
        const originalRequestConfig = error.config

        if (statusCode === 401 && !originalRequestConfig.headers[REQUEST_RETRY_HEADER]) {
            const { url } = error.config
            if (url !== endpoints.auth.signIn && url !== endpoints.auth.signUp && url !== endpoints.auth.getNewToken) {
                return getTokenFromRefresh().then((newToken) => {
                    if (newToken) {
                        originalRequestConfig.headers.Authorization = `Bearer ${newToken}`
                        originalRequestConfig.headers[REQUEST_RETRY_HEADER] = "true"
                        return axiosInstance.request(originalRequestConfig)
                    }
                    toast.error("Please sign in again")
                    setTimeout(() => {
                        window.location.href = paths.auth.jwt.signIn
                    }, 1000)
                    dismissBadInternetToast()
                    return Promise.reject((error.response && error.response.data) || "Something went wrong!")
                })
            }
        }

        if (
            error.code !== "ERR_CANCELED" &&
            error.code !== "ECONNABORTED" &&
            error.code !== "ERR_NETWORK" &&
            error.config.url !== endpoints.personalWhatsapp.disconnect &&
            error.config.url !== endpoints.databaseSavedSearches.list &&
            error.config.url !== endpoints.leads.publicLeadAiStatus &&
            error.config.url !== endpoints.file.parseResume &&
            !error.config.url.startsWith(endpoints.otp.verify) &&
            !error.config.url.endsWith("proctoring-report")
        ) {
            dismissBadInternetToast()
            toast.error(
                error.response?.data
                    ? (error.response.data.errorMessage ??
                          error.response.data.message ??
                          error.response.data.statusMessage ??
                          error.response.data)
                    : "Something went wrong"
            )
        } else if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
            if (!originalRequestConfig.headers[REQUEST_API_TIMEOUT_RETRY_HEADER]) {
                originalRequestConfig.headers[REQUEST_API_TIMEOUT_RETRY_HEADER] = "true"
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(axiosInstance.request(originalRequestConfig))
                    }, 2000)
                })
            }
            showBadInternetToast()
        }
        dismissBadInternetToast()
        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject(
            error.response
                ? { ...error.response.data, httpError: error }
                : { message: "Something went wrong", httpError: error }
        )
    }
)

export default axiosInstance

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
    try {
        const [url, config] = Array.isArray(args) ? args : [args]

        const res = await axiosInstance.get(url, { ...config })

        return res.data
    } catch (error) {
        console.error("Failed to fetch:", error)
        throw error
    }
}

export function removeUserToken() {
    localStorage.removeItem("jwt_access_token")
    localStorage.removeItem("jwt_refresh_token")
    delete axios.defaults.headers.common.Authorization
    delete axiosInstance.defaults.headers.common.Authorization
}

export async function getTokenFromRefresh(): Promise<string | undefined> {
    const refreshToken = localStorage.getItem("jwt_refresh_token")
    if (!refreshToken) {
        removeUserToken()
        return undefined
    }

    try {
        const resp = await axiosInstance.post(
            endpoints.auth.getNewToken,
            { refreshToken },
            {
                transformRequest: (data, headers) => {
                    headers["Content-Type"] = "application/json"
                    delete headers.Authorization
                    return JSON.stringify(data)
                },
            }
        )
        const accessToken = resp?.data?.data?.token
        const newRefreshToken = resp?.data?.data?.refreshToken

        if (accessToken) {
            localStorage.setItem("jwt_access_token", accessToken)
            axiosInstance.defaults.headers.common.Authorization = `Bearer ${accessToken}`
            if (newRefreshToken) localStorage.setItem("jwt_refresh_token", newRefreshToken)
            return accessToken
        }
        removeUserToken()
        return undefined
    } catch (error) {
        removeUserToken()
        return undefined
    }
}

export const isNetworkError = (error: any) => {
    if (error?.httpError) return error?.httpError?.code === "ECONNABORTED" || error?.httpError?.code === "ERR_NETWORK"
    return (
        error?.errorMessage?.clientMessage === "Network Error" ||
        error?.errorMessage?.clientMessage?.includes("timeout")
    )
}

// ----------------------------------------------------------------------

export const endpoints = {
    chat: "/api/chat",
    config: {
        appConfig: "/api/config/status-config",
    },
    personalWhatsapp: {
        whatsappConnectionStatus: "/api/personal-whatsapp/status",
        whitelist: "/api/personal-whatsapp/whitelist",
        connect: "/api/personal-whatsapp/connect",
        disconnect: "/api/personal-whatsapp/disconnect",
        getTemplate: "/api/personal-whatsapp/template",
        sendMessage: "/api/personal-whatsapp/send-message",
    },
    auth: {
        me: "api/users/profile",
        googleLogin: "/api/auth/google/login",
        msLogin: "/api/auth/microsoft/login",
        signIn: "/api/auth/login",
        signUp: "/api/auth/signup",
        users: "/api/users/all",
        getNewToken: "/api/auth/refresh-token",
    },
    selections: {
        list: "/api/selections/all",
        details: "http://localhost:3000/selection",
        statusUpdate: "/api/selections",
        selectionForm: "/api/selections",
    },
    interviews: {
        list: "/api/interviews/all",
        details: "/api/interviews/",
        post: "/api/interviews",
        postBulk: "/api/interviews/bulk",
        update: "/api/interviews",
        roundUpdate: "/api/interviews",
    },
    mandates: {
        list: "/api/mandates/all",
        details: "/api/mandates/",
        jobDetails: "/api/mandates/apna-job-details",
        bulkSource: "/api/bulk-jobs",
        detailsPublic: "/api/public/mandate/",
        update: "/api/mandates",
        draft: "/api/mandates/draft",
        dropdown: "/api/mandates/dropdown",
        activate: "/api/mandates/:id/activate",
        templates: "/api/config/screening-questions-template",
        aiRecruiterType: "/api/mandates/agents",
        aiRecruiterTypeForMandate: (mandateId: string) => `/api/mandates/${mandateId}/recommended-agents`,
        enableAi: (mandateId: string) => `/api/mandates/${mandateId}/agent`,
    },
    clients: {
        list: "/api/clients/all",
        details: "/api/clients/",
        clientForm: "/api/clients",
    },
    leads: {
        list: "/api/leads/all",
        statusUpdate: "/api/leads",
        update: "/api/leads",
        details: "/api/leads/",
        leadsForm: "/api/leads",
        creatLeads: "/api/public-leads/job-link",
        publicCreateLeadsV2: "/api/public-leads/walk-in",
        publicLeadAiStatus: "/api/public-leads/get-ai-status",
        callback: "/api/leads/schedule-callback",
        exportLeads: "/api/bulk-jobs",
        reEvaluate: "/api/lead-scoring/re-evaluate-lead",
        requestPublicInterviewPhoneCall: (leadId: string) => `/api/public-leads/${leadId}/trigger-outbound-call`,
        generateOtpForCandidate: "/api/mandates/generate-otp",
    },
    file: {
        upload: "/api/leads/parse/resume",
        genericUpload: "/api/leads/upload/file?fileType=",
        parseResume: "/api/public/parse/resume",
    },
    pdf: {
        enqueue: "/api/pdf",
        signedUrl: "/api/pdf/signed-url",
    },
    users: {
        getRecruiters: "/api/leads/all",
        list: "/api/users/company-users",
        addUser: "/api/users/create",
        editUser: "/api/users",
        updateUser: "/api/users/update",
        managerList: "/api/users/company/managers",
        rolePermission: "/api/admin/permissions",
        roleAssign: "/api/admin/assign-permission",
        deletePermission: "/api/admin/remove-permission",
        allRoles: "/api/admin/roles",
    },
    bulkUpload: {
        list: "/api/bulk-jobs/all",
        uploadLeads: "/api/bulk-jobs",
        csvUpload: "/api/config/signed-url/file-upload",
        resumeUploadPublic: "/api/public/signed-url/file-upload",
        fileDownload: "/api/config/signed-url/file-download-from-url",
    },
    jobTitle: {
        list: "/api/suggester?size=20",
    },
    ai: {
        generateCandidateEntityDetails: "/api/mandates/extract-mandate-attributes",
        extractMandateDetailsFromPrompt: "/api/mandates/extract-mandate-details",
    },
    onboarding: {
        fetchSteps: "/api/users/onboarding-steps-completion",
        onboardingCompleted: "/api/users/onboarding-completed",
    },
    credits: {
        list: "/api/coins/all",
    },
    otp: {
        verify: "/api/auth/verify-otp",
        send: "/api/auth/send-otp",
    },
    databaseSavedSearches: {
        list: "/api/bulk-jobs/magic-jobs/get-saved-searches",
        count: "/api/bulk-jobs/magic-jobs/get-saved-search-count",
    },
    company: {
        updateLogo: "/api/companies/update-logo",
    },
}
